import {
  Business,
  Category,
  Edit,
  Email,
  Home,
  Map,
  Place,
  Public,
} from '@mui/icons-material';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import { getBusinessSummaryExpress } from 'lib/api/express';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import GeneralEditDialog from './GeneralEditDialog';
import InfoRow from './InfoRow';
import LocationEditDialog from './LocationEditDialog';

interface CompanyData extends CompanyLocation {
  email?: string;
  economicSector?: string;
  economicSubSector?: string;
  economicActivity?: string;
  salesSegment?: string;
}

type CompanyLocation = {
  region?: string;
  province?: string;
  commune?: string;
  address?: string;
};

interface CompanyInformationProps {}

const CompanyInformation: React.FC<CompanyInformationProps> = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [locationEditOpen, setLocationEditOpen] = useState<boolean>(false);
  const [generalEditOpen, setGeneralEditOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const fetchCompanyData = async () => {
    if (!user?.clientId || !user?.businessIdentifier) {
      showAlert(
        dispatch,
        'error',
        'No se ha encontrado la información de la empresa',
      );
      return;
    }
    setLoading(true);
    try {
      const response = await getBusinessSummaryExpress(
        dispatch,
        user.clientId,
        user.businessIdentifier,
      );
      setCompanyData(response);
    } catch (error) {
      console.error('Error fetching company summary:', error);
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al obtener la información de la empresa',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLocationUpdate = (updatedLocation: CompanyLocation) => {
    setCompanyData((prev) => ({
      ...prev,
      ...updatedLocation,
    }));
  };

  const handleGeneralUpdate = (updatedGeneral: CompanyData) => {
    setCompanyData((prev) => ({
      ...prev,
      ...updatedGeneral,
    }));
  };

  useEffect(() => {
    void fetchCompanyData();
  }, [user?.companyId, user?.businessIdentifier]);

  if (loading) return <CenteredCircularProgress relative />;

  return (
    <Paper
      sx={{
        p: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        position: 'relative',
        marginTop: 2,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(25, 118, 210, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                General
              </Typography>
              <IconButton
                size="small"
                onClick={() => setGeneralEditOpen(true)}
                disabled={
                  !companyData ||
                  (!user?.companyName &&
                    !user?.businessIdentifier &&
                    !companyData?.email)
                }
                sx={{
                  padding: 0.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  },
                }}
              >
                <Edit sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              </IconButton>
            </Box>
            <InfoRow
              label="Razón Social"
              value={user?.companyName ?? null}
              icon={
                <Business sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              }
            />
            <InfoRow
              label="RUT"
              value={user?.businessIdentifier ?? null}
              icon={
                <Business sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              }
            />
            <InfoRow
              label="Email"
              value={companyData?.email ?? null}
              icon={
                <Email sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(255, 152, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'warning.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                Ubicación
              </Typography>
              <IconButton
                size="small"
                onClick={() => setLocationEditOpen(true)}
                disabled={
                  !companyData ||
                  (!companyData.region &&
                    !companyData.province &&
                    !companyData.commune &&
                    !companyData.address)
                }
                sx={{
                  padding: 0.5,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(158, 158, 158, 0.1)',
                  },
                }}
              >
                <Edit sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
              </IconButton>
            </Box>
            <InfoRow
              label="Region"
              value={companyData?.region ?? null}
              icon={
                <Public sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
              }
            />
            <InfoRow
              label="Province"
              value={companyData?.province ?? null}
              icon={<Map sx={{ fontSize: '0.8rem', color: 'warning.main' }} />}
            />
            <InfoRow
              label="Commune"
              value={companyData?.commune ?? null}
              icon={
                <Place sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
              }
            />
            <InfoRow
              label="Address"
              value={companyData?.address ?? null}
              maxLength={45}
              icon={<Home sx={{ fontSize: '0.8rem', color: 'warning.main' }} />}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(76, 175, 80, 0.15)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'success.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}
              >
                Información Económica
              </Typography>
            </Box>
            <InfoRow
              label="Economic Sector"
              value={companyData?.economicSector ?? null}
              maxLength={40}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
            />
            <InfoRow
              label="Economic Subsector"
              value={companyData?.economicSubSector ?? null}
              maxLength={40}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
            />
            <InfoRow
              label="Economic Activity"
              value={companyData?.economicActivity ?? null}
              maxLength={40}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
            />
            <InfoRow
              label="Sales Segment"
              value={companyData?.salesSegment ?? null}
              maxLength={40}
              icon={
                <Business sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
            />
          </Box>
        </Grid>
      </Grid>

      <LocationEditDialog
        open={locationEditOpen}
        onClose={() => setLocationEditOpen(false)}
        onSave={handleLocationUpdate}
        initialData={{
          region: companyData?.region,
          province: companyData?.province,
          commune: companyData?.commune,
          address: companyData?.address,
        }}
      />

      <GeneralEditDialog
        open={generalEditOpen}
        onClose={() => setGeneralEditOpen(false)}
        onSave={handleGeneralUpdate}
        initialData={{
          name: user?.companyName,
          taxIdentifier: user?.businessIdentifier,
          email: companyData?.email,
        }}
      />
    </Paper>
  );
};

export default CompanyInformation;
